import { useRef } from "react";
import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { ImageCarousel } from "../../components/ImageCarousel";
import { UnrecognisedScan } from "../../components/UnrecognisedScan";
import { useIsMobile } from "../../components/use-is-mobile";
import { formatAmount } from "../../core/primitives/money";
import { SCAN_STAGES } from "../../core/primitives/scan-progress";
import { ScanEditor } from "../../sections/scan-editor/scan-editor";
import { ScanEditorState } from "../../sections/scan-editor/scan-editor-model";
import { ScanCardActions, ScanCardState } from "./scan-card-model";

// ─── sub-components ─────────────────────────────────────────────────────────

function UploadArea({
  label,
  required,
  preview,
  onFile,
}: {
  label: string;
  required?: boolean;
  preview: string | null;
  onFile: (f: File) => void;
}) {
  return (
    <label className="relative flex min-h-[200px] flex-1 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-row border-2 border-dashed border-line bg-surface transition-colors hover:border-accent">
      {preview ? (
        <>
          <img src={preview} alt={`${label} of the card`} className="absolute inset-0 h-full w-full object-contain" />
          <div className="absolute inset-0 flex items-end p-2">
            <span className="rounded bg-black/60 px-1.5 py-0.5 text-xs text-white">{label}</span>
          </div>
        </>
      ) : (
        <>
          <div className="mb-2 text-4xl text-faint">+</div>
          <p className="text-sm font-medium text-white">
            {label}
            {required ? (
              <span className="ml-0.5 text-danger">*</span>
            ) : (
              <span className="ml-1 font-normal text-muted">(optional)</span>
            )}
          </p>
          <p className="mt-1 text-xs text-faint">Choose a photo</p>
        </>
      )}
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
    </label>
  );
}

function CameraArea({ label, preview, onFile }: { label: string; preview: string | null; onFile: (f: File) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="aspect-[4/3] w-full overflow-hidden rounded-row border border-line bg-surface">
        {preview ? (
          <img src={preview} alt={`${label} of the card`} className="h-full w-full object-contain" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-center">
              <div className="mb-2 text-5xl opacity-40">📷</div>
              <p className="text-sm text-faint">No photo yet</p>
            </div>
          </div>
        )}
      </div>
      <button type="button" onClick={() => ref.current?.click()} className="btn-primary w-full">
        {preview ? "Retake photo" : `Take photo of the ${label}`}
      </button>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

const STEP_LABELS = ["Upload", "Review", "Done"] as const;

export function ScanCardPage() {
  const state = useSubtree(ScanCardState);
  const actions = useSubtree(ScanCardActions);
  const editor = useSubtree(ScanEditorState);
  const observer = useObserver();
  const isMobile = useIsMobile();

  const noMatch = observer.watch(state.noMatch);
  const hasStore = observer.watch(state.hasStore);
  const step = observer.watch(state.step);
  const mobileSubstep = observer.watch(state.mobileSubstep);
  const frontPreview = observer.watch(state.frontPreview);
  const backPreview = observer.watch(state.backPreview);
  const scanProgress = observer.watch(state.scanProgress);
  const scanError = observer.watch(state.scanError);
  const confirmed = observer.watch(state.confirmed);
  const restoring = observer.watch(state.restoring);
  const reviewed = observer.watch(editor.scan);

  // A scan reopened after a refresh has no local previews, only what was uploaded.
  const imageUrls = (frontPreview ? [frontPreview, backPreview] : [reviewed?.imageUrl, reviewed?.backImageUrl]).filter(
    Boolean,
  ) as string[];
  const confirmedPrice = confirmed?.priceCents != null ? formatAmount(confirmed.priceCents, null) : "No price set";
  const confirmedPriceCls =
    confirmed?.priceCents != null ? "text-2xl font-bold tabular-nums text-accent" : "text-base font-medium text-muted";

  // ── step renders ──────────────────────────────────────────────────────────

  if (noMatch)
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <UnrecognisedScan scan={noMatch} onNewScan={actions.newScan} />
      </div>
    );

  const renderStep1Desktop = () => (
    <div className="mx-auto max-w-2xl">
      <h2 className="mb-1 text-lg font-semibold text-white">Add photos of the card</h2>
      <p className="mb-6 text-sm text-muted">Lay it flat in even light, with the whole card in frame.</p>
      <div className="mb-6 flex gap-4">
        <UploadArea label="Front" required preview={frontPreview} onFile={actions.setFront} />
        <UploadArea label="Back" preview={backPreview} onFile={actions.setBack} />
      </div>
      {scanError && (
        <p className="mb-4 text-sm text-danger" role="alert">
          {scanError}
        </p>
      )}
      <button type="button" onClick={() => actions.scan()} disabled={!frontPreview} className="btn-primary w-full">
        Scan card
      </button>
    </div>
  );

  const renderStep1Mobile = () => (
    <div className="mx-auto max-w-sm">
      <div className="mb-6 flex items-center gap-2">
        {([1, 2] as const).map((s, i) => (
          <div key={s} className="flex flex-1 items-center">
            <div
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                mobileSubstep === s
                  ? "bg-accent text-ink"
                  : mobileSubstep > s
                    ? "bg-accent/40 text-white"
                    : "bg-surface-strong text-faint"
              }`}
            >
              {s}
            </div>
            {i < 1 && <div className={`mx-2 h-0.5 flex-1 ${mobileSubstep > 1 ? "bg-accent" : "bg-line"}`} />}
          </div>
        ))}
      </div>
      {mobileSubstep === 1 ? (
        <>
          <h2 className="mb-1 text-base font-semibold text-white">Photograph the front</h2>
          <p className="mb-4 text-sm text-muted">Lay it flat in even light, with the whole card in frame.</p>
          <CameraArea label="front" preview={frontPreview} onFile={actions.setFront} />
          {scanError && (
            <p className="mt-3 text-sm text-danger" role="alert">
              {scanError}
            </p>
          )}
          <button
            type="button"
            onClick={actions.toBackStep}
            disabled={!frontPreview}
            className="btn-primary mt-4 w-full"
          >
            Continue to the back
          </button>
        </>
      ) : (
        <>
          <h2 className="mb-1 text-base font-semibold text-white">Photograph the back</h2>
          <p className="mb-4 text-sm text-muted">Optional. The front alone is enough to scan.</p>
          <CameraArea label="back" preview={backPreview} onFile={actions.setBack} />
          {scanError && (
            <p className="mt-3 text-sm text-danger" role="alert">
              {scanError}
            </p>
          )}
          <div className="mt-4 flex flex-col gap-2">
            <button type="button" onClick={() => actions.scan(false)} className="btn-primary w-full">
              Scan card
            </button>
            {backPreview && (
              <button type="button" onClick={() => actions.scan(true)} className="btn-secondary w-full">
                Scan without the back
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="flex flex-col items-center justify-center py-12">
      {frontPreview ? (
        <div className="mb-6 h-48 w-36 overflow-hidden rounded-row border border-line bg-surface">
          <img src={frontPreview} alt="Front of the card being scanned" className="h-full w-full object-contain" />
        </div>
      ) : (
        <div className="mb-5 h-12 w-12 animate-spin rounded-full border-4 border-line border-t-accent" />
      )}
      <p className="text-base font-semibold text-white" role="status">
        Scanning card…
      </p>
      <ol className="mt-4 space-y-2 text-sm">
        {SCAN_STAGES.map(({ key, label }) => {
          const status = scanProgress.stages[key];
          return (
            <li key={key} className="flex items-center gap-2">
              <span
                className={
                  status === "complete"
                    ? "h-2 w-2 rounded-full bg-accent"
                    : status === "active"
                      ? "h-2 w-2 animate-pulse rounded-full bg-accent"
                      : status === "error"
                        ? "h-2 w-2 rounded-full bg-danger"
                        : "h-2 w-2 rounded-full bg-surface-strong"
                }
              />
              <span className={status === "pending" ? "text-faint" : "text-white"}>{label}</span>
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-sm text-faint">Identifying and pricing take a few seconds each.</p>
      <button type="button" onClick={actions.cancelScan} className="btn-ghost mt-6">
        Cancel scan
      </button>
    </div>
  );

  const renderRestoring = () => (
    <div className="flex flex-col items-center justify-center py-24" role="status">
      <div className="mb-5 h-12 w-12 animate-spin rounded-full border-4 border-line border-t-accent" />
      <p className="text-base font-semibold text-white">Reopening your scan…</p>
    </div>
  );

  // Confirming created the card, and stocked it when the reviewer asked, so
  // there is nothing left to press here — only somewhere to go next.
  const doneActions = (
    <div className="space-y-3">
      <p className="note note-accent text-accent">
        {state.customerId ? (
          "Approved. It is on the customer's list, ready to add to their cart."
        ) : confirmed?.stocked ? (
          "Added to your catalogue and your inventory."
        ) : (
          <>
            Added to your catalogue. Its stock is not counted yet —{" "}
            <Link to={`/cards/${confirmed?.cardId}`} className="underline underline-offset-2">
              track stock on the card
            </Link>
            .
          </>
        )}
      </p>
      {state.customerId ? (
        <Link
          to={`/customer-scans?customer=${encodeURIComponent(state.customerId)}`}
          className="btn-primary flex w-full"
        >
          Back to the customer
        </Link>
      ) : (
        <Link to="/cards" className="btn-secondary flex w-full">
          View cards
        </Link>
      )}
    </div>
  );

  const renderStep5Desktop = () => (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-start gap-5">
        <div className="flex shrink-0 gap-3">
          {imageUrls.map((url, i) => (
            <div key={url} className="h-32 w-24 shrink-0 overflow-hidden rounded-row border border-line bg-surface">
              <img src={url} alt={`Card ${i + 1}`} className="h-full w-full object-contain" />
            </div>
          ))}
        </div>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-white [overflow-wrap:anywhere]">{confirmed?.title}</h2>
          <p className={`mt-1 ${confirmedPriceCls}`}>{confirmedPrice}</p>
        </div>
      </div>
      {doneActions}
      <div className="mt-5">
        <button type="button" onClick={actions.newScan} className="btn-secondary w-full">
          Scan next card
        </button>
      </div>
    </div>
  );

  const renderStep5Mobile = () => (
    <div className="mx-auto max-w-sm">
      <ImageCarousel urls={imageUrls} />
      <h2 className="text-lg font-bold text-white [overflow-wrap:anywhere]">{confirmed?.title}</h2>
      <p className={`mb-6 mt-0.5 ${confirmedPriceCls}`}>{confirmedPrice}</p>
      {doneActions}
      <div className="mt-5">
        <button type="button" onClick={actions.newScan} className="btn-secondary w-full">
          Scan next card
        </button>
      </div>
    </div>
  );

  // ── step indicator ────────────────────────────────────────────────────────

  // Scanning already counts toward Review: the photos are done.
  const stepIndex = step === 1 ? 0 : step === 5 ? 2 : 1;

  // The scan flow needs an active store to scope every request; without one
  // there is nothing meaningful to render.
  if (!hasStore) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="panel p-10 text-center">
          <p className="text-sm font-semibold text-white">No active store</p>
          <p className="mt-1 text-xs text-muted">Select or connect a store to start scanning cards.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {!restoring && (
        <div className="mx-auto mb-8 flex max-w-2xl items-center">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex flex-1 items-center last:flex-none">
              <div className="flex shrink-0 items-center gap-2">
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                    i === stepIndex
                      ? "bg-accent text-ink"
                      : i < stepIndex
                        ? "bg-accent/40 text-white"
                        : "bg-surface-strong text-faint"
                  }`}
                >
                  {i + 1}
                </div>
                <span
                  className={`hidden text-xs font-medium sm:block ${i === stepIndex ? "text-accent" : "text-faint"}`}
                >
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className={`mx-3 h-0.5 flex-1 ${i < stepIndex ? "bg-accent" : "bg-line"}`} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="panel p-6">
        {restoring && renderRestoring()}
        {!restoring && step === 1 && (isMobile ? renderStep1Mobile() : renderStep1Desktop())}
        {step === 2 && renderStep2()}
        {step === 3 && (
          <ScanEditor
            isMobile={isMobile}
            confirmLabel="Confirm"
            cancelLabel="Start over"
            onCancel={actions.newScan}
            showReject={false}
          />
        )}
        {step === 5 && (isMobile ? renderStep5Mobile() : renderStep5Desktop())}
      </div>
    </div>
  );
}
